import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Typography, TableRow, Skeleton } from '@chilecompra/react-kit/components'
import { PUBLIC_URL } from '../../config/settings/environment'
import { GENERAL_INFORMATION_TABS } from '../../config/settings/constant'
import { onGetInstitutionalManagementThunk } from './InstitutionalManagementSection.actions'
import {
  Card,
  Column,
  Root,
  Row,
  Separator,
  Strong,
  Table,
  TableCell,
  CardItem,
  WarningImg
} from './InstitutionalManagementSection.styles'

/**
 * The Institutional Management section container.
 */
const InstitutionalManagementSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isTiny }
  } = useViewport()
  const {
    loading,
    error,
    representativeList = [],
    administratorList = []
  } = useSelector(state => state.institutionalManagement)

  useEffectOnce(() => {
    dispatch(onGetInstitutionalManagementThunk({ dni }))
  }, [dni, dispatch])

  return (
    <Root>
      <Row>
        <Column width="100%" margin="0 0 20px 0">
          <>
            <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
              {!loading ? (
                'Administración de la institución'
              ) : (
                <Skeleton height="35px" variant="rectangular" width="50%" />
              )}
            </Typography>
            {!loading ? (
              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                <Strong>Fuente:</Strong> Información ingresada por el organismo público en Mercado Público.
              </Typography>
            ) : (
              <Skeleton height="15px" variant="rectangular" width="70%" />
            )}
          </>
        </Column>
      </Row>
      {!loading && error ? (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="16px"
              lineHeight="21px"
              margin="0 0 0 33px"
              tabIndex="0"
              sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
            >
              {GENERAL_INFORMATION_TABS.GENERAL_INFORMATION.SERVICE_ERROR}
            </Typography>
          </CardItem>
        </Card>
      ) : (
        <>
          <Row>
            <Column xs={12} sm={6} md={6} lg={4}>
              <Typography
                color="black1"
                fontSize="16px"
                fontWeight="bold"
                variant="h4"
                lineHeight="21px"
                tabIndex="0"
                sx={{ maxWidth: '280px' }}
              >
                {!loading ? (
                  'Personas con rol administrador en Mercado Público'
                ) : (
                  <Skeleton height="35px" variant="rectangular" width="70%" />
                )}
              </Typography>
            </Column>
            <Column xs={12} sm={6}>
              {!loading && administratorList.length > 0 && (
                <Table accessibility={{ label: 'Rol Administrador' }}>
                  {administratorList.map((authorities, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell disableBottomBorder tabIndex="0">
                          {authorities.firstName + ' ' + authorities.lastName}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Table>
              )}
              {!loading && administratorList.length === 0 && (
                <Typography
                  color="black1"
                  fontSize="14px"
                  fontWeight="regular"
                  variant="body2"
                  lineHeight="18px"
                  tabIndex="0"
                  sx={{ padding: { xs: '14px 9px 10px 0px', sm: '14px 9px 10px 9px' } }}
                >
                  {GENERAL_INFORMATION_TABS.AUTHORITIES.SERVICE_ERROR}
                </Typography>
              )}
              {loading && (
                <>
                  <Skeleton height="15px" margin="10px 0" variant="rectangular" width="100%" />
                  <Skeleton height="15px" variant="rectangular" width="100%" />
                </>
              )}
            </Column>
          </Row>
          <Row>
            <Column xs={12} sm={6} md={6} lg={4}>
              <Typography
                color="black1"
                fontSize="16px"
                fontWeight="bold"
                variant="body2"
                lineHeight="21px"
                tabIndex="0"
                sx={{ maxWidth: '280px' }}
              >
                {!loading ? 'Autoridad representante' : <Skeleton height="35px" variant="rectangular" width="70%" />}
              </Typography>
            </Column>
            <Column xs={12} sm={6}>
              {!loading && representativeList.length > 0 && (
                <Table accessibility={{ label: 'Autoridad representante' }} padding="0" margin="0">
                  {representativeList.map((authorities, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell disableBottomBorder tabIndex="0">
                          {authorities.firstName + ' ' + authorities.lastName}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Table>
              )}
              {!loading && representativeList.length === 0 && (
                <Typography
                  color="black1"
                  fontSize="14px"
                  fontWeight="regular"
                  variant="body2"
                  lineHeight="18px"
                  tabIndex="0"
                  sx={{ padding: { xs: '12px 9px 10px 0px', sm: '12px 9px 10px 9px' } }}
                >
                  {GENERAL_INFORMATION_TABS.AUTHORITIES.NO_REPRESENTATIVE_AUTHORITY}
                </Typography>
              )}
              {loading && <Skeleton height="15px" margin="10px 0" variant="rectangular" width="100%" />}
            </Column>
          </Row>
        </>
      )}
      <Separator />
    </Root>
  )
}

export default InstitutionalManagementSection
